import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useBriefDock } from '@/app/providers/BriefDockContext';
import { NOTIFICATION_EVENT, type NotificationToastInput } from './notify';
import { clampToastMessage } from './clamp-toast-message';
import './NotificationToast.css';

interface ActiveToast extends NotificationToastInput {
  id: string;
}

const AUTO_DISMISS_MS = 6000;
const MAX_VISIBLE = 3;

/**
 * Renders the stack of in-app notification toasts (top-right). Listens for the
 * `xavior:notification` window event (see notify.ts), slides each toast in,
 * auto-dismisses after a few seconds, and — on "View" — opens the Notification/
 * Activity Center via the dock (works on desktop AND mobile). Mounted once at
 * the app shell, inside BriefDockProvider.
 */
export function NotificationToastHost() {
  const { openBrief } = useBriefDock();
  const [toasts, setToasts] = useState<ActiveToast[]>([]);
  const timers = useRef<Map<string, number>>(new Map());

  const dismiss = useCallback((id: string) => {
    setToasts((cur) => cur.filter((t) => t.id !== id));
    const tm = timers.current.get(id);
    if (tm) {
      window.clearTimeout(tm);
      timers.current.delete(id);
    }
  }, []);

  useEffect(() => {
    const onNotify = (e: Event) => {
      const detail = (e as CustomEvent<NotificationToastInput>).detail;
      if (!detail?.title) return;
      const id = detail.id ?? `nt-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      setToasts((cur) => [{ ...detail, id }, ...cur.filter((t) => t.id !== id)].slice(0, MAX_VISIBLE));
      const tm = window.setTimeout(() => dismiss(id), AUTO_DISMISS_MS);
      timers.current.set(id, tm);
    };
    window.addEventListener(NOTIFICATION_EVENT, onNotify);
    const pending = timers.current;
    return () => {
      window.removeEventListener(NOTIFICATION_EVENT, onNotify);
      pending.forEach((tm) => window.clearTimeout(tm));
      pending.clear();
    };
  }, [dismiss]);

  const view = useCallback(
    (id: string) => {
      // Open the Notification/Activity Center through the dock state machine —
      // rides the bell→panel flight on desktop, opens instantly on mobile, and
      // no-ops if it's already open. (Was a desktop-only `.header-brief-bell`
      // click, which did nothing on mobile.)
      openBrief();
      dismiss(id);
    },
    [openBrief, dismiss],
  );

  if (typeof document === 'undefined' || toasts.length === 0) return null;

  return createPortal(
    <div className="act-toast-stack" role="region" aria-label="Notifications">
      {toasts.map((t) => (
        <div key={t.id} className="act-toast" role="status">
          {t.icon ? (
            <span className="act-toast-icon" aria-hidden="true">
              {t.icon}
            </span>
          ) : null}
          <div className="act-toast-body">
            {t.actor ? <span className="act-toast-actor">{t.actor}</span> : null}
            <span className="act-toast-title">{t.title}</span>
            {t.message ? (
              <span className="act-toast-message">{clampToastMessage(t.message)}</span>
            ) : null}
          </div>
          <div className="act-toast-actions">
            <button type="button" className="act-toast-view" onClick={() => view(t.id)}>
              {t.actionLabel ?? 'View'}
            </button>
            <button
              type="button"
              className="act-toast-close"
              aria-label="Dismiss"
              onClick={() => dismiss(t.id)}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                aria-hidden="true"
              >
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      ))}
    </div>,
    document.body,
  );
}
